import { useRef, useState } from "react";
import { Printer, X, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import CustomSelect from "@/components/ui/CustomSelect";

export default function BillModal({ order, session, settings, onClose, onSettled }) {
  const [waPhone, setWaPhone] = useState("");
  const [showWa, setShowWa] = useState(false);
  const [payMode, setPayMode] = useState("cash");
  const [settling, setSettling] = useState(false);
  const printRef = useRef(null);
  const sitting = session || null;
  if (!sitting && !order) return null;
  const s = settings || {};
  const items = sitting
    ? (Array.isArray(sitting.lines) && sitting.lines.length
        ? sitting.lines
        : (sitting.orders || []).flatMap((o) =>
            o.status === "cancelled" ? [] : (o.items || [])
          ))
    : (Array.isArray(order.items) ? order.items : []);
  const subtotal = Number(
    sitting?.current_total ??
      order?.amount ??
      items.reduce((sum, it) => sum + (it.qty || 0) * (it.price || 0), 0)
  ) || 0;
  const gstRate = (s.gst_rate ?? 0) / 100;
  const cgst = +(subtotal * gstRate / 2).toFixed(2);
  const sgst = +(subtotal * gstRate / 2).toFixed(2);
  const total = +(subtotal + cgst + sgst).toFixed(2);
  const billNo = sitting?.session_code
    ? `B-${sitting.session_code}`
    : `B-${order?.order_number ?? "?"}`;
  const tableNum = sitting?.table ?? order?.table;
  const tableText = tableNum === 0 || tableNum == null ? "Walk-in" : `Table ${tableNum}`;
  const dateStr = (sitting?.opened_at || order?.created_at)
    ? new Date(sitting?.opened_at || order.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
    : "—";
  const closed = sitting
    ? sitting.status === "closed"
    : ["paid", "cancelled"].includes(order?.status);
  const settleSessionId = sitting?.id || order?.session_id;

  const handlePrint = () => {
    const el = printRef.current;
    if (!el) {
      toast.error("Bill preview isn't ready yet. Try again.");
      return;
    }

    // Hidden iframe — no popup (Chrome returns null for window.open + noopener).
    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", `Bill ${billNo}`);
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;";
    document.body.appendChild(iframe);

    const win = iframe.contentWindow;
    const doc = win?.document;
    if (!win || !doc) {
      iframe.remove();
      toast.error("Couldn't open the print dialog. Try again.");
      return;
    }

    const title = `Bill ${billNo}`;
    doc.open();
    doc.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>
    @page { size: 80mm auto; margin: 4mm; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #111;
      font-family: 'Courier New', Courier, monospace;
      font-size: 12px;
      line-height: 1.35;
    }
    body { width: 72mm; max-width: 72mm; padding: 2mm; }
    img { max-width: 80px; max-height: 80px; object-fit: contain; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: none; padding: 2px 0; font-size: 12px; color: #000; }
    .b-center { text-align: center; }
    .b-line { border-top: 1px dashed #000; margin: 8px 0; }
    @media print {
      html, body { width: 72mm; background: #fff; }
    }
  </style>
</head>
<body>${el.innerHTML}</body>
</html>`);
    doc.close();

    const cleanup = () => {
      try { iframe.remove(); } catch { /* ignore */ }
    };

    const doPrint = () => {
      try {
        win.focus();
        win.print();
      } catch {
        toast.error("Couldn't open the print dialog. Try again.");
        cleanup();
        return;
      }
      // Keep iframe until print dialog closes (Save as PDF / print).
      if (typeof win.onafterprint !== "undefined") {
        win.onafterprint = cleanup;
      }
      setTimeout(cleanup, 60_000);
    };

    // Wait for layout (and logo image if present) before printing.
    const imgs = Array.from(doc.images || []);
    const waitImages = imgs.length
      ? Promise.all(
          imgs.map(
            (img) =>
              img.complete
                ? Promise.resolve()
                : new Promise((resolve) => {
                    img.onload = resolve;
                    img.onerror = resolve;
                  })
          )
        )
      : Promise.resolve();

    waitImages.then(() => setTimeout(doPrint, 150));
  };

  const buildWhatsAppMessage = () => {
    const lines = [];
    lines.push(`*${s.restaurant_name || "ZenTaap Restaurant"}*`);
    if (s.address) lines.push(s.address);
    if (s.phone) lines.push(`Ph: ${s.phone}`);
    if (s.gst_number) lines.push(`GSTIN: ${s.gst_number}`);
    lines.push(`-------------------------`);
    lines.push(`Bill #${billNo}  |  ${tableText}`);
    lines.push(dateStr);
    lines.push(`-------------------------`);
    for (const it of items) {
      lines.push(`${it.name} x${it.qty}  ₹${((it.qty || 0) * (it.price || 0)).toFixed(2)}`);
    }
    lines.push(`-------------------------`);
    lines.push(`Subtotal: ₹${subtotal.toFixed(2)}`);
    if (gstRate > 0) {
      lines.push(`CGST (${(s.gst_rate / 2).toFixed(1)}%): ₹${cgst.toFixed(2)}`);
      lines.push(`SGST (${(s.gst_rate / 2).toFixed(1)}%): ₹${sgst.toFixed(2)}`);
    }
    lines.push(`*TOTAL: ₹${total.toFixed(2)}*`);
    lines.push(``);
    lines.push(`Thank you for dining with us! 🙏`);
    return lines.join("\n");
  };

  const sendWhatsApp = () => {
    const digits = waPhone.replace(/[^0-9]/g, "");
    if (digits.length < 7) {
      setShowWa(true);
      return;
    }
    const msg = encodeURIComponent(buildWhatsAppMessage());
    const num = digits.length === 10 ? `91${digits}` : digits;
    window.open(`https://wa.me/${num}?text=${msg}`, "_blank");
  };

  return (
    <div
      className="bill-modal-overlay"
      onClick={onClose}
      data-testid="bill-modal"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        className="bill-modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--card)",
          border: "1px solid var(--line)",
          borderRadius: 14,
          padding: 18,
          maxWidth: 420,
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div
          className="no-print"
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}
        >
          <div className="font-serif" style={{ fontSize: 18, color: "var(--gold)" }}>
            Bill Preview
          </div>
          <button
            onClick={onClose}
            className="mini-btn"
            data-testid="bill-close-btn"
            style={{ padding: 6 }}
          >
            <X size={14} />
          </button>
        </div>

        <div
          ref={printRef}
          className="bill-print"
          style={{
            background: "white",
            color: "#111",
            padding: 16,
            borderRadius: 8,
            fontFamily: "'Courier New', monospace",
            fontSize: 13,
          }}
          data-testid="bill-print-area"
        >
          {s.logo_url && (
            <div className="b-center" style={{ textAlign: "center", marginBottom: 6 }}>
              <img
                src={s.logo_url}
                alt="logo"
                style={{ maxWidth: 80, maxHeight: 80, objectFit: "contain" }}
              />
            </div>
          )}
          <div className="b-center" style={{ textAlign: "center", fontWeight: 700, fontSize: 16 }}>
            {s.restaurant_name || "ZenTaap Restaurant"}
          </div>
          {s.address && (
            <div className="b-center" style={{ textAlign: "center", fontSize: 11 }}>
              {s.address}
            </div>
          )}
          {s.phone && (
            <div className="b-center" style={{ textAlign: "center", fontSize: 11 }}>
              Ph: {s.phone}
            </div>
          )}
          {s.gst_number && (
            <div className="b-center" style={{ textAlign: "center", fontSize: 11 }}>
              GSTIN: {s.gst_number}
            </div>
          )}
          <div className="b-line" style={{ borderTop: "1px dashed #000", margin: "8px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
            <span>Bill #{billNo}</span>
            <span>{tableText}</span>
          </div>
          <div style={{ fontSize: 11, marginBottom: 4 }}>{dateStr}</div>
          <div className="b-line" style={{ borderTop: "1px dashed #000", margin: "8px 0" }} />

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "2px 0", fontSize: 12, color: "#000", border: "none" }}>Item</th>
                <th style={{ textAlign: "right", padding: "2px 0", fontSize: 12, color: "#000", border: "none" }}>Qty</th>
                <th style={{ textAlign: "right", padding: "2px 0", fontSize: 12, color: "#000", border: "none" }}>Amt</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={`${it.name}-${i}`}>
                  <td style={{ padding: "2px 0", fontSize: 12, color: "#000", border: "none" }}>{it.name}</td>
                  <td style={{ padding: "2px 0", textAlign: "right", fontSize: 12, color: "#000", border: "none" }}>{it.qty}</td>
                  <td style={{ padding: "2px 0", textAlign: "right", fontSize: 12, color: "#000", border: "none" }}>
                    ₹{((it.qty || 0) * (it.price || 0)).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="b-line" style={{ borderTop: "1px dashed #000", margin: "8px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
            <span>Subtotal</span>
            <span>₹{subtotal.toFixed(2)}</span>
          </div>
          {gstRate > 0 && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span>CGST ({(s.gst_rate / 2).toFixed(1)}%)</span>
                <span>₹{cgst.toFixed(2)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span>SGST ({(s.gst_rate / 2).toFixed(1)}%)</span>
                <span>₹{sgst.toFixed(2)}</span>
              </div>
            </>
          )}
          <div className="b-line" style={{ borderTop: "1px dashed #000", margin: "8px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 14 }}>
            <span>TOTAL</span>
            <span>₹{total.toFixed(2)}</span>
          </div>
          <div className="b-line" style={{ borderTop: "1px dashed #000", margin: "8px 0" }} />
          <div className="b-center" style={{ textAlign: "center", fontSize: 11, marginTop: 6 }}>
            Thank you for dining with us!
          </div>
        </div>

        <div className="no-print" style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
          {!closed && (
            <div style={{ width: "100%", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <CustomSelect
                value={payMode}
                onChange={setPayMode}
                options={[
                  { value: "cash", label: "Cash" },
                  { value: "upi", label: "UPI" },
                  { value: "card", label: "Card" },
                  { value: "other", label: "Other" },
                ]}
                data-testid="bill-pay-mode"
                style={{ flex: "1 1 160px" }}
              />
              <button
                type="button"
                className="submit-btn"
                data-testid="bill-mark-paid-btn"
                disabled={settling}
                onClick={async () => {
                  setSettling(true);
                  try {
                    if (settleSessionId) {
                      await api.post(`/table-sessions/${settleSessionId}/settle`, {
                        payment_mode: payMode,
                      });
                    } else if (order?.id) {
                      await api.post(`/orders/${order.id}/settle`, {
                        payment_mode: payMode,
                        clear_table: true,
                      });
                    }
                    toast.success(
                      tableNum > 0
                        ? `Marked paid (${payMode}) · Table ${tableNum} closed`
                        : `Marked paid (${payMode})`
                    );
                    onSettled?.();
                    onClose();
                  } catch (err) {
                    toast.error(friendlyError(err, "Couldn't settle this bill."));
                  } finally {
                    setSettling(false);
                  }
                }}
                style={{ flex: "2 1 160px", background: "var(--green, #22c55e)", color: "#fff" }}
              >
                {settling ? "Saving…" : tableNum > 0 ? "Mark Paid & Close Table" : "Mark Paid"}
              </button>
            </div>
          )}
          <button
            onClick={handlePrint}
            className="submit-btn"
            data-testid="bill-print-btn"
            style={{ flex: 1, minWidth: 140 }}
          >
            <Printer size={14} style={{ display: "inline", marginRight: 6, verticalAlign: "middle" }} />
            Print / Download
          </button>
          <button
            onClick={() => setShowWa((v) => !v)}
            className="submit-btn"
            data-testid="bill-whatsapp-btn"
            style={{ flex: 1, minWidth: 140, background: "#25D366", color: "white" }}
          >
            <MessageCircle size={14} style={{ display: "inline", marginRight: 6, verticalAlign: "middle" }} />
            Send via WhatsApp
          </button>
          <button onClick={onClose} className="submit-btn ghost" data-testid="bill-cancel-btn">
            Close
          </button>
        </div>
        {showWa && (
          <div
            className="no-print"
            data-testid="whatsapp-input-row"
            style={{
              marginTop: 12,
              padding: 12,
              background: "var(--bg)",
              border: "1px solid var(--line)",
              borderRadius: 10,
            }}
          >
            <label className="form-label" style={{ marginBottom: 6, display: "block" }}>
              Customer&apos;s WhatsApp number
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="tel"
                value={waPhone}
                onChange={(e) => setWaPhone(e.target.value)}
                placeholder="e.g. 9876543210"
                data-testid="whatsapp-phone-input"
                style={{
                  flex: 1,
                  background: "var(--card)",
                  border: "1px solid var(--line)",
                  color: "var(--text)",
                  padding: "10px 12px",
                  borderRadius: 8,
                  fontSize: 14,
                  outline: "none",
                }}
              />
              <button
                type="button"
                className="submit-btn"
                onClick={sendWhatsApp}
                data-testid="whatsapp-send-btn"
                style={{ background: "#25D366", color: "white" }}
              >
                Send
              </button>
            </div>
            <div style={{ color: "var(--muted)", fontSize: 11, marginTop: 6 }}>
              Opens WhatsApp with the bill pre-filled. India: 10 digits or with country code.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
